// Settlement Calculator Component
// BP-10-04: Attorney Frontend

import { useState, useEffect, useMemo } from 'react';
import {
  Calculator,
  DollarSign,
  Percent,
  Calendar,
  Plus,
  Trash2,
  TrendingUp,
  Info,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { useSettlementCalculatorStore } from '@/features/attorney/stores/attorneyStore';

interface StructuredPayment {
  id: string;
  type: 'lump_sum' | 'periodic' | 'life_contingent';
  amount: number;
  startDate?: string;
  frequency?: 'monthly' | 'annual';
  duration?: number;
  growthRate?: number;
  presentValue?: number;
}

interface SettlementCalculatorProps {
  caseId: string;
  lifetimeCostEstimate?: number;
  onSave?: (data: any) => void;
}

export function SettlementCalculator({
  caseId,
  lifetimeCostEstimate = 0,
  onSave,
}: SettlementCalculatorProps) {
  const store = useSettlementCalculatorStore();
  
  const [grossSettlement, setGrossSettlement] = useState<string>('');
  const [attorneyFeePercent, setAttorneyFeePercent] = useState<string>('33.33');
  const [caseExpenses, setCaseExpenses] = useState<string>('');
  const [liens, setLiens] = useState<string>('');
  const [discountRate, setDiscountRate] = useState<string>('3');
  const [structuredPayments, setStructuredPayments] = useState<StructuredPayment[]>([]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const parseCurrency = (value: string): number => {
    return parseFloat(value.replace(/[^0-9.-]/g, '')) || 0;
  };

  // Calculate distribution
  const distribution = useMemo(() => {
    const gross = parseCurrency(grossSettlement);
    const feePercent = parseFloat(attorneyFeePercent) || 0;
    const expenses = parseCurrency(caseExpenses);
    const lienAmount = parseCurrency(liens);

    const attorneyFees = gross * (feePercent / 100);
    const netBeforeLiens = gross - attorneyFees - expenses;
    const netToClient = netBeforeLiens - lienAmount;

    return {
      gross,
      attorneyFees,
      feePercent,
      expenses,
      liens: lienAmount,
      netBeforeLiens,
      netToClient,
    };
  }, [grossSettlement, attorneyFeePercent, caseExpenses, liens]);

  // Calculate present value of structured payments
  const calculatePresentValue = (payment: StructuredPayment): number => {
    const rate = parseFloat(discountRate) / 100;
    
    if (payment.type === 'lump_sum') {
      return payment.amount;
    }

    if (payment.type === 'periodic' && payment.frequency && payment.duration) {
      const periodsPerYear = payment.frequency === 'monthly' ? 12 : 1;
      const totalPeriods = payment.duration * periodsPerYear;
      const periodicRate = rate / periodsPerYear;
      const periodicAmount = payment.amount / periodsPerYear;

      // Present value of annuity with growth
      const growth = (payment.growthRate || 0) / 100;
      let pv = 0;
      for (let i = 1; i <= totalPeriods; i++) {
        const adjustedAmount = periodicAmount * Math.pow(1 + growth / periodsPerYear, i - 1);
        pv += adjustedAmount / Math.pow(1 + periodicRate, i);
      }
      return pv;
    }

    return payment.amount;
  };

  const structuredTotal = useMemo(() => {
    return structuredPayments.reduce((sum, p) => sum + (p.presentValue || p.amount), 0);
  }, [structuredPayments]);

  // Update present values when discount rate changes
  useEffect(() => {
    setStructuredPayments(prev =>
      prev.map(p => ({
        ...p,
        presentValue: calculatePresentValue(p),
      }))
    );
  }, [discountRate]);

  const addStructuredPayment = (type: StructuredPayment['type']) => {
    const newPayment: StructuredPayment = {
      id: Date.now().toString(),
      type,
      amount: 0,
      frequency: type === 'periodic' ? 'annual' : undefined,
      duration: type === 'periodic' ? 20 : undefined,
      growthRate: type === 'periodic' ? 2 : undefined,
    };
    setStructuredPayments([...structuredPayments, newPayment]);
  };

  const updateStructuredPayment = (id: string, updates: Partial<StructuredPayment>) => {
    setStructuredPayments(prev =>
      prev.map(p => {
        if (p.id === id) {
          const updated = { ...p, ...updates };
          updated.presentValue = calculatePresentValue(updated);
          return updated;
        }
        return p;
      })
    );
  };

  const removeStructuredPayment = (id: string) => {
    setStructuredPayments(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Gross Settlement Input */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-navy flex items-center gap-2 mb-4">
          <Calculator className="w-5 h-5 text-sage" />
          Settlement Distribution
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Inputs */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gross Settlement Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={grossSettlement}
                  onChange={(e) => setGrossSettlement(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attorney Fee Percentage
              </label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  value={attorneyFeePercent}
                  onChange={(e) => setAttorneyFeePercent(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  step="0.01"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Case Expenses
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={caseExpenses}
                  onChange={(e) => setCaseExpenses(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Liens (Medicare, Medicaid, etc.)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={liens}
                  onChange={(e) => setLiens(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage/50 focus:border-sage"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Distribution Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-700 mb-3">Distribution Summary</h4>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Gross Settlement</span>
              <span className="font-medium">{formatCurrency(distribution.gross)}</span>
            </div>
            
            <div className="flex justify-between text-sm text-red-600">
              <span>Attorney Fees ({distribution.feePercent}%)</span>
              <span>- {formatCurrency(distribution.attorneyFees)}</span>
            </div>
            
            <div className="flex justify-between text-sm text-red-600">
              <span>Case Expenses</span>
              <span>- {formatCurrency(distribution.expenses)}</span>
            </div>
            
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Net Before Liens</span>
                <span className="font-medium">{formatCurrency(distribution.netBeforeLiens)}</span>
              </div>
            </div>
            
            <div className="flex justify-between text-sm text-red-600">
              <span>Liens</span>
              <span>- {formatCurrency(distribution.liens)}</span>
            </div>
            
            <div className="border-t-2 border-sage pt-2">
              <div className="flex justify-between">
                <span className="font-semibold text-navy">Net to Client</span>
                <span className="font-bold text-xl text-sage">
                  {formatCurrency(distribution.netToClient)}
                </span>
              </div>
            </div>

            {lifetimeCostEstimate > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-blue-800 font-medium">
                      Life Care Plan: {formatCurrency(lifetimeCostEstimate)}
                    </p>
                    <p className="text-blue-600 text-xs mt-1">
                      Coverage: {((distribution.netToClient / lifetimeCostEstimate) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Structured Settlement */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-navy flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-sage" />
            Structured Settlement Modeling
          </h3>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Discount Rate:</label>
            <input
              type="number"
              value={discountRate}
              onChange={(e) => setDiscountRate(e.target.value)}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
              step="0.1"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </div>

        {/* Add Payment Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => addStructuredPayment('lump_sum')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Lump Sum
          </button>
          <button
            onClick={() => addStructuredPayment('periodic')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Periodic Payment
          </button>
          <button
            onClick={() => addStructuredPayment('life_contingent')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Life Contingent
          </button>
        </div>

        {/* Structured Payments List */}
        {structuredPayments.length > 0 ? (
          <div className="space-y-3">
            {structuredPayments.map((payment, index) => (
              <div
                key={payment.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 grid md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Type
                      </label>
                      <span className="text-sm font-medium text-navy capitalize">
                        {payment.type.replace('_', ' ')}
                      </span>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        {payment.type === 'periodic' ? 'Annual Amount' : 'Amount'}
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                        <input
                          type="number"
                          value={payment.amount || ''}
                          onChange={(e) =>
                            updateStructuredPayment(payment.id, {
                              amount: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded text-sm"
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {payment.type === 'periodic' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Duration (years)
                          </label>
                          <input
                            type="number"
                            value={payment.duration || ''}
                            onChange={(e) =>
                              updateStructuredPayment(payment.id, {
                                duration: parseInt(e.target.value) || 0,
                              })
                            }
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Annual Growth
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={payment.growthRate || ''}
                              onChange={(e) =>
                                updateStructuredPayment(payment.id, {
                                  growthRate: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                              step="0.1"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              %
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => removeStructuredPayment(payment.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {payment.presentValue && payment.presentValue !== payment.amount && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between text-sm">
                    <span className="text-gray-500">Present Value</span>
                    <span className="font-medium text-sage">
                      {formatCurrency(payment.presentValue)}
                    </span>
                  </div>
                )}
              </div>
            ))}

            {/* Structured Total */}
            <div className="bg-sage/10 rounded-lg p-4 flex justify-between items-center">
              <span className="font-medium text-navy">
                Total Present Value (Structured)
              </span>
              <span className="text-xl font-bold text-sage">
                {formatCurrency(structuredTotal)}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Add structured payment components above</p>
          </div>
        )}
      </div>

      {/* Save Button */}
      {onSave && (
        <div className="flex justify-end">
          <button
            onClick={() =>
              onSave({
                distribution,
                structuredPayments,
                structuredTotal,
                discountRate: parseFloat(discountRate),
              })
            }
            className="px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
          >
            Save Settlement Analysis
          </button>
        </div>
      )}
    </div>
  );
}

export default SettlementCalculator;

